import React, { useState } from "react";
import { IconType } from "react-icons";
import { FiInfo } from "react-icons/fi";
import { GoArrowDown, GoArrowUp, GoArrowUpRight, GoDash } from "react-icons/go";
import { GBButtonWithIcon } from "../CTAButtons";
import { pricingTableData } from "./constants";
import { useLayout } from "../Layout";

interface PlanDetails {
    name: string;
    price: string;
    pricePeriod: string;
    btnText: string;
    color?: string;
    bgColor?: string;
}

export interface PricingHeaderData {
    siteHeader: string;
    siteText: string;
    planData: PlanDetails[];
    isDollar?: boolean;
}

interface PricingTableDetails {
    title: string;
    tooltip?: string;
    starter: string | IconType;
    growth: string | IconType;
    scale: string | IconType;
    starterTooltip?: string;
    growthTooltip?: string;
    scaleTooltip?: string;
    pro?: string | IconType;
    proTooltip?: string;
}

export interface PricingTableData {
    tableTitle: string;
    tableDetails: PricingTableDetails[];
}

const PricingTable: React.FC<PricingHeaderData> = (props) => {
    const { siteHeader, siteText, planData, isDollar } = props;
    const [openSections, setOpenSections] = useState(() => pricingTableData(isDollar as boolean).map((_, index) => index));
    const { showBanner } = useLayout();
    const toggleSection = (index: number) => {
        setOpenSections((prev) => (prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]));
    };

    return (
        <React.Fragment>
            <div
                className={`bg-white z-1 ${showBanner ? "sticky-position" : "sticky-position-no-banner"}`}
                style={{
                    animation: "showTable 200ms ease-in-out forwards",
                    borderTop: "1px solid black",
                    borderLeft: "1px solid black",
                    borderRight: "1px solid black",
                }}
            >
                <div className="block lg:hidden py-2 space-y-2" style={{ borderBottom: "1px solid black" }}>
                    <h3 className="text-3xl font-bold">{siteHeader}</h3>
                    {/* <p className="px-2">{siteText}</p> */}
                    <div className="flex md:gap-7 md:flex justify-between" style={{ color: "#3364FA" }}>
                        {planData.map((data, idx) => (
                            <h4 key={idx} className={`font-medium text-center text-sm ${isDollar ? "w-28" : "md:w-auto w-28"}`}>
                                {data.name}
                            </h4>
                        ))}
                    </div>
                </div>

                {isDollar ? (
                    <div className="lg:flex md:gap-28 w-full p-5 hidden" style={{ borderBottom: "1px solid black" }}>
                        <div className="py-5">
                            <h3 className="text-2xl font-bold pb-3">{siteHeader}</h3>
                        </div>

                        <section className="flex gap-28">
                            {planData.map((data, idx) => (
                                <div key={idx} className="gap-1">
                                    <h4
                                        className="text-base font-semibold md:mb-0 flex gap-1 items-center"
                                        style={isDollar ? undefined : { color: data.name === "GROWTH" ? "" : "" }}
                                    >
                                        {data.name}
                                        <p className="rounded-full px-2 font-bold text-purple-900 text-sm" style={{ background: data.bgColor, color: data.color }}>
                                            {data.price}
                                        </p>
                                    </h4>
                                    <p className="text-black-900 text-sm md:mb-1">{data.pricePeriod}</p>

                                    <GBButtonWithIcon
                                        rightIcon={GoArrowUpRight}
                                        target="_blank"
                                        href="https://app.gallabox.com/signup"
                                        rel="noreferrer"
                                        variant="primary-dark"
                                        style={{
                                            fontSize: "14px",
                                            width: "215px",
                                            padding: "3px 6px",
                                            justifyContent: "space-between",
                                        }}
                                    >
                                        {data.btnText}
                                    </GBButtonWithIcon>
                                </div>
                            ))}
                        </section>
                    </div>
                ) : (
                    <div className={"lg:flex border-solid border-700 border-b py-3 hidden"}>
                        <p className="flex items-center justify-start md:p-5 py-2 px-4 large">
                            <h3 className="text-2xl font-bold">{siteHeader}</h3>
                        </p>
                        <div className={"md:flex md:gap-32 flex justify-between px-4 py-1"}>
                            {planData.map((data, idx) => (
                                <div key={idx} className="gap-1">
                                    <h4
                                        className="text-base font-semibold md:mb-2 flex gap-1 items-center"
                                        style={isDollar ? undefined : { color: data.name === "GROWTH" ? "" : "" }}
                                    >
                                        {data.name}
                                        <p className="rounded-full px-2 font-bold text-purple-900 text-sm" style={{ background: data.bgColor, color: data.color }}>
                                            {data.price}
                                        </p>
                                    </h4>

                                    <GBButtonWithIcon
                                        rightIcon={GoArrowUpRight}
                                        target="_blank"
                                        href="https://app.gallabox.com/signup"
                                        rel="noreferrer"
                                        variant="primary-dark"
                                        style={{
                                            fontSize: "14px",
                                            width: "215px",
                                            padding: "3px 6px",
                                            justifyContent: "space-between",
                                        }}
                                    >
                                        {data.btnText}
                                    </GBButtonWithIcon>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {pricingTableData(isDollar as boolean).map((data, index) => (
                <div key={index} className="">
                    <h6
                        className="flex justify-between md:pl-5 pl-4 py-4 pr-10"
                        style={{
                            backgroundColor: "#EDF2F7",
                            borderBottom: "1px solid black",
                            borderLeft: "1px solid black",
                            borderRight: "1px solid black",
                        }}
                        onClick={() => toggleSection(index)}
                    >
                        {data.tableTitle}
                        {openSections.includes(index) ? <GoArrowUp fontSize="16px" /> : <GoArrowDown fontSize="16px" />}
                    </h6>

                    {openSections.includes(index) && (
                        <>
                            {data.tableDetails.map((tableData, idx) => (
                                <div key={idx} className={`md:flex border-solid border-700 border-l border-r border-b  ${isDollar ? "gap-10" : ""}`}>
                                    <p className="flex items-center justify-start md:p-5 py-2 px-4 large">
                                        <p className="text-sm md:text-base">{tableData.title}</p>
                                        {tableData.tooltip && (
                                            <div className="relative group ml-2 inline-block">
                                                <FiInfo className="text-base cursor-pointer" />
                                                <span className="absolute bottom-full -right-24 mb-2 w-56 break-words bg-gray-700 text-white text-sm px-2 py-1 rounded shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200">
                                                    {tableData.tooltip}
                                                </span>
                                            </div>
                                        )}
                                    </p>
                                    <div className={"md:flex md:gap-32 flex justify-between px-4"}>
                                        {/* {!isDollar ? (
                                            <p className=" items-center justify-center flex  text-center large-screen-width pr-5">
                                                <TableDataItem value={tableData.starter} tooltip={tableData.starterTooltip} />
                                            </p>
                                        ) : null} */}
                                        <p className=" items-center justify-center flex  text-center large-screen-width pr-5">
                                            <TableDataItem value={tableData.growth} tooltip={tableData.growthTooltip} />
                                        </p>
                                        <p className=" items-center justify-center flex text-center large-screen-width pr-5">
                                            <TableDataItem value={tableData.scale} tooltip={tableData.scaleTooltip} />
                                        </p>
                                        <p className=" items-center justify-center flex  text-center large-screen-width pr-5">
                                            <TableDataItem value={tableData?.pro ?? ""} tooltip={tableData.proTooltip} />
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </>
                    )}
                </div>
            ))}
        </React.Fragment>
    );
};

export default PricingTable;

interface TableDataItemProps {
    value: string | React.ComponentType<{ fontSize: string; color: string }>;
    tooltip?: string;
}

const TableDataItem: React.FC<TableDataItemProps> = (props) => {
    const { value, tooltip } = props;
    return (
        <div
            className={` ${
                typeof value === "string" ? "p-1 md:flex md:flex-row md:items-center md:justify-center gap-2 flex  items-center" : "p-1 flex items-center justify-center gap-2"
            }`}
        >
            {typeof value === "string" ? value : React.createElement(value, { fontSize: value === GoDash ? "27px" : "", color: value === GoDash ? "gray" : "#3364FA" })}
            {tooltip && (
                <div className="relative group inline-block">
                    <FiInfo className="text-base cursor-pointer"> </FiInfo>
                    <span className="absolute break-words bg-gray-700 text-white text-sm px-2 py-2 rounded shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200">
                        {tooltip}
                    </span>
                </div>
            )}
        </div>
    );
};
